import { useDispatch, useSelector } from "react-redux";
import { change, getFormValues } from "redux-form";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { PAC_FIELDS_BY_AD_TYPE } from "social/constants/placements";

export function updatePlacements(adType, placements, fieldName, isDynamic){
	/*
		This returns a copy of placements where fields matching `fieldName` in placements
		have their `isDynamic` property set to the value provided.

		`placements` is expected in a format like:
		```
			{
				Square: {
					...
					primaryText: {
						isDynamic: false,
					}
				},
				Vertical: {
					...
					primaryText: {
						isDynamic: false,
						value: "vertical primary text",
					}
				},
			}
		```

		`fieldName` is expected to be a string like "primaryText", rather than the full field
		name like "facebookImageMO.primaryText".
	*/
	const placementList = Object.keys(placements);

	return placementList.reduce((prev, placementName) => {
		const pacFields = PAC_FIELDS_BY_AD_TYPE[adType]?.[placementName] || [];

		if(!pacFields.includes(fieldName)){
			return prev;
		}

		const placementValue = prev[placementName][fieldName];
		if(typeof(placementValue) === "object" || placementValue === undefined){
			prev[placementName][fieldName] = {
				...placementValue,
				isDynamic,
			};
		} else {
			prev[placementName][fieldName] = {
				value: placementValue,
				isDynamic,
			};
		}
		return prev;
	}, { ...placements });
}

export default function usePlacementDynamicUpdate(adType){
	/*
		Only the default placement is allowed to change the dynamic setting of Asset fields,
		and it must update the other placements to match when changed.

		This hook returns an `onChange` handler that will spread the `isDynamic` property from
		the default placement to all other placements that are present.
	*/
	const dispatch = useDispatch();
	const formValues = useSelector(getFormValues(SOCIAL_WIZARD)) || {};
	const adTypeValues = formValues[adType] || {};
	const placements = adTypeValues.placements || {};

	return (alsoValue, value, previousValue, fieldName) => {
		/*
			The first argument to a redux-form Field onChange handler is supposed to be the change event,
			but this appears to get overwritten by the value in Rhombus.
		*/
		const fieldBaseName = fieldName.split(".").at(-1);
		const updatedPlacements = updatePlacements(adType, placements, fieldBaseName, value.isDynamic);

		dispatch(change(SOCIAL_WIZARD, `${adType}.placements`, updatedPlacements));
	};
}
